import { useCallback } from 'react';
import { DeleteIllustration } from '@/components/modals/DeleteIllustration';
import { useModal } from '@/hooks/use-modal';
import type { ReactNode } from 'react';

export interface ConfirmDeleteOptions {
    title: ReactNode;
    description?: ReactNode;
    /** Defaults to 'Excluir'. Pass a verb that matches the object being removed. */
    confirmText?: string;
    cancelText?: string;
    /**
     * Mirrors `ModalFooterConfig.onAccept`: the callee decides when to close, so
     * async handlers can close inside their own `try` after the request settles.
     */
    onConfirm: (close: () => void) => void | Promise<void>;
    onCancel?: () => void;
}

/**
 * The single entry point for "are you sure you want to delete this?".
 *
 * Everything these confirmations share — the destructive tone, the illustration
 * in the body, the outline/destructive button pair and the default labels —
 * lives here, so a call site only supplies what actually differs: the copy and
 * what to delete.
 */
export const useConfirmDelete = () => {
    const { openModal } = useModal();

    return useCallback(
        ({
            title,
            description,
            confirmText = 'Excluir',
            cancelText = 'Cancelar',
            onConfirm,
            onCancel,
        }: ConfirmDeleteOptions) => {
            openModal({
                // Deliberately no `variant: 'destructive'`. The variant exists to
                // paint an icon chip beside the title, and the illustration below
                // already carries that signal — a chip too would say it twice. The
                // red confirm button is set explicitly instead of inherited.
                title,
                description,
                content: <DeleteIllustration />,
                footer: {
                    closeText: cancelText,
                    closeVariant: 'outline',
                    acceptText: confirmText,
                    acceptVariant: 'destructive',
                    onClose: onCancel,
                    onAccept: onConfirm,
                },
            });
        },
        [openModal],
    );
};
